import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Badge, Table, Tooltip, Typography } from 'antd'
import type { ColumnsType, TableProps } from 'antd/es/table'
import { FilterFilled } from '@ant-design/icons'
import { DateTime } from 'luxon'
import styled from 'styled-components'

import { MODULE_ID } from '@qflow/constants'
import { colours, sizes } from '@qflow/theme'

import {
  ALERT_MAPPER,
  ALERT_STATUS_MAPPER,
  moduleNameFromId
} from 'portal/components/RecordsModules/common/alerts'
import type {
  AlertType,
  InfractionAlertType,
  ProjectAlertType
} from 'portal/components/RecordsModules/common/alerts'
import { getRecordLinkUri } from 'portal/components/RecordsTable/RecordsColumn/RecordLink'

import { DateFormatFull } from 'portal/lib/functions'
import { useBeamerBarHeight } from 'portal/lib/hooks'
import { ColourButton, icons, Spacer, UserAvatar } from 'portal/lib/primitives'
import { trackMixpanel } from 'portal/store/mixpanel/actions'
import { projectAlertClientDismiss } from 'portal/store/projectAlerts/actions'

import { ActionStrip } from './ActionStrip'
import { PROJECT_LEVEL_ALERTS_PREFIX } from './constants'
import {
  BTTButton,
  ButtonContainer,
  ShowingAlertsText,
  StyledClickableLink,
  TableWrapper,
  TableFooter,
  ZeroRecordsFooter
} from './StyledElements'
import { handleApiFailure } from 'portal/lib/sharedComponents/apiFailureHandler'

type OnChange = NonNullable<TableProps<any>['onChange']>
type Filters = Parameters<OnChange>[1]

export const AlertsTable = ({
  showDismissed,
  allowSelectionAndAction,
  reload
}) => {
  const [filteredDataCount, setFilteredDataCount] = useState(0)
  const [filteredInfo, setFilteredInfo] = useState<Filters>({})

  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
  const [eventRecordMapping, setEventRecordMapping] = useState({})
  const [assignableUsers, setAssignableUsers] = useState<any[]>([])

  const dispatch = useDispatch()
  const { accountId, projectId } = useParams<{
    accountId: string
    projectId: string
  }>()

  const CONSTANTS = {
    CLOSED: 'Closed'
  }

  const projectTimeZone = useSelector(
    (state: any) => state.projectDetails.modules?.timeZone
  )

  const projectUsers = useSelector((state: any) => state.projectUsers)
  const allRecordAlerts = useSelector((state: any) => state.recordAlerts)
  const allProjectAlerts = useSelector((state: any) => state.projectAlerts)

  type User = {
    userId: string
    displayName: string
  }

  const users: User[] = Object.values(projectUsers.users)

  const formattedRecordAlertsData = useMemo(() => {
    if (Array.isArray(allRecordAlerts?.data)) {
      const filteredAlerts = allRecordAlerts.data.filter((alert: AlertType) => {
        // Filter out unmapped alerts
        if (!ALERT_MAPPER[alert.recordAlertType]) {
          return false
        }

        // Filter by dismissed status
        return (
          showDismissed === 'true' ||
          alert.status !== ALERT_STATUS_MAPPER.clientDismissed
        )
      })
      return filteredAlerts.map(alert => ({
        eventKey: alert.recordAlertId,
        createdAt: alert.recordDate,
        moduleName: moduleNameFromId(alert.moduleTagDefinitionId),
        moduleTagDefinitionId: alert.moduleTagDefinitionId,
        type: ALERT_MAPPER[alert.recordAlertType]?.type
          ? ALERT_MAPPER[alert.recordAlertType].type
          : '',
        description: `${ALERT_MAPPER[alert.recordAlertType]?.description}${
          alert.triggeredSetting
            ? `${ALERT_MAPPER[alert.recordAlertType]?.description ? ': ' : ''}${alert.triggeredSetting}`
            : ''
        }`,
        destinationDisplayName: alert.recordDescription,
        assignedTo: alert.assignedTo,
        recordId: alert.recordId,
        status:
          alert.status === ALERT_STATUS_MAPPER.clientDismissed
            ? CONSTANTS.CLOSED
            : alert.status,
        isProjectLevelAlert: false,
        isRecordLevelAlert: true
      }))
    }
    return []
  }, [CONSTANTS.CLOSED, allRecordAlerts.data, showDismissed])

  const formattedProjectAlertsData = useMemo(() => {
    if (Array.isArray(allProjectAlerts?.data)) {
      const filteredAlerts = allProjectAlerts.data.filter(
        (alert: ProjectAlertType) => {
          // Filter out unmapped alerts
          if (!ALERT_MAPPER[alert.recordAlertType]) {
            return false
          }

          // Filter by dismissed status
          return (
            showDismissed === 'true' ||
            alert.status !== ALERT_STATUS_MAPPER.clientDismissed
          )
        }
      )
      return filteredAlerts.map(alert => ({
        eventKey: alert.projectAlertId,
        createdAt: alert.alertedAt,
        moduleName: moduleNameFromId(alert.moduleTagDefinitionId),
        moduleTagDefinitionId: alert.moduleTagDefinitionId,
        type: ALERT_MAPPER[alert.recordAlertType]?.type
          ? ALERT_MAPPER[alert.recordAlertType].type
          : '',
        description: `${ALERT_MAPPER[alert.recordAlertType]?.description}${
          alert.triggeredSetting
            ? `${ALERT_MAPPER[alert.recordAlertType]?.description ? ': ' : ''}${alert.triggeredSetting}`
            : ''
        }`,
        tradeContractor: alert.tradeContractor,
        supplier: alert.supplier,
        assignedTo: alert.assignedTo,
        recordId: undefined,
        status:
          alert.status === ALERT_STATUS_MAPPER.clientDismissed
            ? CONSTANTS.CLOSED
            : alert.status,
        isProjectLevelAlert: true,
        isRecordLevelAlert: false
      }))
    }

    return []
  }, [CONSTANTS.CLOSED, allProjectAlerts.data, showDismissed])

  // Combine record and project alerts and sort by date
  const formattedAlertsData = useMemo(() => {
    const combinedAlerts = [
      ...formattedRecordAlertsData,
      ...formattedProjectAlertsData
    ]
    return combinedAlerts.sort((a, b) => {
      const dateA = DateTime.fromISO(a.recordDate, { zone: 'local' })
      const dateB = DateTime.fromISO(b.recordDate, { zone: 'local' })
      return dateB.toMillis() - dateA.toMillis() // Sort descending by date
    })
  }, [formattedRecordAlertsData, formattedProjectAlertsData])

  const { Text } = Typography

  // Filter out dismissed alerts if showDismissed is false
  const formattedDisplayItems: any[] = useMemo(() => {
    return showDismissed === 'true'
      ? formattedAlertsData
      : formattedAlertsData.filter(item => item.status !== CONSTANTS.CLOSED)
  }, [CONSTANTS.CLOSED, formattedAlertsData, showDismissed])

  // Create an object whose keys is the eventKey
  // and value is an object containing the recordId, and
  // an array of who is assigned to that record
  useEffect(() => {
    const tempMapping = {}

    // Add record level alerts to the mapping
    formattedRecordAlertsData.forEach(alert => {
      tempMapping[alert.eventKey] = {
        recordId: alert.recordId,
        assignedTo: alert.assignedTo,
        description: alert.description,
        subcontractor: alert.subcontractor,
        wasteCarrier: alert.wasteCarrier,
        isRecordLevelAlert: alert.isRecordLevelAlert
      }
    })

    setEventRecordMapping(tempMapping)
  }, [formattedRecordAlertsData])

  // Reformat ProjectUsers for use in AssigneeIds filter
  const UNASSIGNED = 'unassigned'
  useEffect(() => {
    const otherAssignedToFilters = [
      { value: UNASSIGNED, text: <i>Unassigned</i> }
    ]

    const tempArray = Object.values(projectUsers.users)
      .map((user: any) => ({
        value: user.userId as string,
        text: user.displayName as string
      }))
      .sort((a, b) => {
        if (a.text === null) {
          return 1
        }
        if (b.text === null) {
          return -1
        }
        const fa = a.text.toLowerCase()
        const fb = b.text.toLowerCase()

        if (fa < fb) {
          return -1
        } else if (fa > fb) {
          return 1
        }
        return 0
      })

    setAssignableUsers([...otherAssignedToFilters, ...tempArray])
  }, [projectUsers.users])

  const tableId = 'AlertsTable'

  const handleProjectLevelAlertDismiss = useCallback(
    (alert: ProjectAlertType) => {
      dispatch(
        trackMixpanel('frontend_project_alert_dismissed', {
          account_id: accountId,
          project_id: projectId,
          record_type: moduleNameFromId(alert.moduleTagDefinitionId),
          alert_type: alert.recordAlertType
        })
      )
      if (accountId && projectId) {
        dispatch(
          projectAlertClientDismiss(
            accountId,
            projectId,
            alert.projectAlertId as string,
            () => {}, // onSuccess
            handleApiFailure // onError
          )
        )
        // Reload project alerts after dismissing
        setTimeout(() => {
          reload()
        }, 250)
      }
    },
    [accountId, dispatch, projectId, reload]
  )

  const scrollToTop = () => {
    const tableContent = document.querySelector('.ant-table-body')
    // Check if the tableContent is null
    if (!tableContent) return

    // Scroll to the top of the table
    tableContent.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth'
    })
  }

  const usedHeight = 370 + useBeamerBarHeight()

  const renderDescription = (text, item) => {
    if (!item.subDescription) {
      return text
    }
    return (
      <div>
        <div>{text}</div>
        <div className="subText">{item.subDescription}</div>
      </div>
    )
  }

  const recordAlertTypeFilters = useMemo(() => {
    const filters = Object.keys(ALERT_MAPPER)
      .map(key => ({
        text: ALERT_MAPPER[key].type,
        value: ALERT_MAPPER[key].type
      }))
      .sort((a, b) => {
        return a.text.localeCompare(b.text)
      })

    return filters
  }, [])

  const handleResetFilters = () => {
    setFilteredInfo({})
  }

  // If all filters are null, or empty array, disable the reset button
  const allFiltersEmpty = Object.entries(filteredInfo).every(
    ([key, filter]) => {
      return filter === null || (Array.isArray(filter) && filter.length === 0)
    }
  )

  // Count the number of active filters to display in the badge
  const activeFilterCount = Object.entries(filteredInfo).reduce(
    (count, [key, filter]) => {
      if (
        (typeof filter === 'string' && filter !== '') ||
        (Array.isArray(filter) && filter.length > 0)
      ) {
        return count + 1
      }
      return count
    },
    0
  )

  const columns: ColumnsType<InfractionAlertType> = useMemo(() => {
    function setCellId(alert, rowIndex) {
      if (alert.status === CONSTANTS.CLOSED) {
        return {
          style: {
            textDecoration: 'line-through',
            opacity: '0.66'
          }
        }
      }
      return {}
    }

    return [
      {
        title: 'Date Created',
        dataIndex: 'createdAt',
        key: 'createdAt',
        sorter: (a, b) => {
          const aValue = a.createdAt || ''
          const bValue = b.createdAt || ''
          return aValue.localeCompare(bValue)
        },
        defaultSortOrder: 'descend',
        onCell: setCellId,
        width: '8.5rem',
        render: (text, record) => {
          return DateTime.fromISO(text, { zone: 'local' }).toLocaleString(
            DateFormatFull(projectTimeZone) as Intl.DateTimeFormatOptions,
            { locale: Intl.DateTimeFormat().resolvedOptions().locale }
          )
        }
      },
      {
        title: 'Module',
        dataIndex: 'moduleTagDefinitionId',
        key: 'moduleTagDefinitionId',
        onCell: setCellId,
        width: '6.25rem',
        render: text => {
          return <CapitalisedText>{moduleNameFromId(text)}</CapitalisedText>
        },
        filters: [
          { text: 'Delivery', value: MODULE_ID.DELIVERY },
          { text: 'Waste', value: MODULE_ID.WASTE }
        ],
        filteredValue: filteredInfo.moduleTagDefinitionId || null,
        onFilter: (value, record) => record.moduleTagDefinitionId === value,
        filterIcon: filtered => {
          const count = filteredInfo.moduleTagDefinitionId?.length || 0
          return (
            <Tooltip
              title={
                count > 0
                  ? `${count} filter option${count > 1 ? 's' : ''} applied`
                  : 'No filters applied'
              }
            >
              <span>
                <FilterFilled
                  style={{ color: filtered ? colours.TEAL : undefined }}
                />
              </span>
            </Tooltip>
          )
        }
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
        onCell: setCellId,
        render: (text, item) => (text ? text : 'Issue'),
        filters: recordAlertTypeFilters,
        filteredValue: filteredInfo.type || null,
        onFilter: (value, record) =>
          record.type === value || record.sourceType === value,
        filterIcon: filtered => {
          const count = filteredInfo.type?.length || 0
          return (
            <Tooltip
              title={
                count > 0
                  ? `${count} filter option${count > 1 ? 's' : ''} applied`
                  : 'No filters applied'
              }
            >
              <span>
                <FilterFilled
                  style={{ color: filtered ? colours.TEAL : undefined }}
                />
              </span>
            </Tooltip>
          )
        }
      },
      {
        title: 'Description',
        dataIndex: 'description',
        key: 'description',
        onCell: setCellId,
        render: (text, item) => renderDescription(text, item),
        width: '30%'
      },
      {
        title: (
          <span>
            Delivery ID / Consignment N<sup>o</sup>
          </span>
        ),
        dataIndex: 'destinationDisplayName',
        key: 'destinationDisplayName',
        sorter: (a, b) => {
          const aValue = a.destinationDisplayName || ''
          const bValue = b.destinationDisplayName || ''
          return aValue.localeCompare(bValue)
        },
        onCell: setCellId,
        render: text => {
          return <Text>{text ?? '-'}</Text>
        }
      },
      {
        title: 'Assigned To',
        dataIndex: 'assignedTo',
        className: 'assignedTo',
        filters: assignableUsers,
        filteredValue: filteredInfo.assignedTo || null,
        filterSearch: true,
        onFilter: (value, record) => {
          if (record.assignedTo && record.assignedTo.length > 0) {
            return record.assignedTo.some(user => user.userId === value)
          }
          return value === UNASSIGNED
        },
        filterIcon: filtered => {
          const count = filteredInfo.assignedTo?.length || 0
          return (
            <Tooltip
              title={
                count > 0
                  ? `${count} filter option${count > 1 ? 's' : ''} applied`
                  : 'No filters applied'
              }
            >
              <span>
                <FilterFilled
                  style={{ color: filtered ? colours.TEAL : undefined }}
                />
              </span>
            </Tooltip>
          )
        },
        render: (assignedToArray, record) => {
          if (record.isProjectLevelAlert) {
            return (
              <Tooltip title="This type of alert cannot be assigned">
                <span style={{ padding: '0 0.75rem' }}>&ndash;</span>
              </Tooltip>
            )
          }
          const userAvatars = assignedToArray?.map((user: any) => {
            const matchedUser: any = users
              ? users.find((u: any) => u.userId === user.userId)
              : null

            return matchedUser ? (
              <UserAvatar displayName={matchedUser.displayName} />
            ) : (
              <UserAvatar displayName={'Qflow System'} />
            )
          })
          return userAvatars
        },
        width: '8rem',
        align: 'center'
      },
      {
        title: (
          <Tooltip
            title={
              allFiltersEmpty ? 'No filters to clear' : 'Clear all filters'
            }
          >
            <ResetFilters
              aria-label={
                allFiltersEmpty ? 'No filters to clear' : 'Clear all filters'
              }
              disabled={allFiltersEmpty}
              onClick={handleResetFilters}
            >
              <Badge
                count={allFiltersEmpty ? 0 : activeFilterCount}
                offset={[-1, 1]}
                color={colours.TEAL}
                styles={{
                  root: {
                    color: 'inherit'
                  }
                }}
              >
                <icons.ResetFilters size="22px" />
              </Badge>
            </ResetFilters>
          </Tooltip>
        ),
        width: '4rem',
        dataIndex: 'recordId',
        key: 'recordId',
        render: (text, record) => {
          if (!accountId || !projectId || !text) {
            // If not already dismissed show dismiss button
            if (record.status !== CONSTANTS.CLOSED && allowSelectionAndAction) {
              return (
                <Tooltip title="Dismiss alert">
                  <ColourButton.NegativeSecondary
                    aria-label="Dismiss alert"
                    style={{ padding: '2px 7px', minHeight: '1.75rem' }}
                    onClick={() =>
                      handleProjectLevelAlertDismiss(
                        allProjectAlerts.data.find(
                          alert => alert.projectAlertId === record.eventKey
                        )
                      )
                    }
                  >
                    <icons.TrashCan
                      colour="currentColor"
                      size={sizes.X_SMALL}
                    />
                  </ColourButton.NegativeSecondary>
                </Tooltip>
              )
            }

            return null
          }
          return (
            <StyledClickableLink
              id="navigateToDetailsBtn"
              to={getRecordLinkUri(
                {
                  accountId,
                  projectId,
                  moduleName: record.moduleName,
                  module: record.moduleName
                },
                record.moduleName,
                record.recordId
              )}
            >
              <Tooltip title="View record">
                <icons.ViewRecord />
              </Tooltip>
            </StyledClickableLink>
          )
        }
      }
    ]
  }, [
    filteredInfo.moduleTagDefinitionId,
    filteredInfo.type,
    filteredInfo.assignedTo,
    recordAlertTypeFilters,
    assignableUsers,
    allFiltersEmpty,
    activeFilterCount,
    CONSTANTS.CLOSED,
    projectTimeZone,
    Text,
    users,
    accountId,
    projectId,
    allowSelectionAndAction,
    handleProjectLevelAlertDismiss,
    allProjectAlerts.data
  ])

  const handleTableChange = (pagination, filters, sorter, extra) => {
    // `extra.currentDataSource` contains the filtered data
    setFilteredInfo(filters)
    setFilteredDataCount(extra.currentDataSource.length)
  }

  const onSelectChange = (newSelectedRowKeys: React.Key[]) => {
    setSelectedRowKeys(newSelectedRowKeys)
  }

  type TableRowSelection<T extends object = object> =
    TableProps<T>['rowSelection']

  const rowSelection: TableRowSelection<any> = {
    selectedRowKeys,
    onChange: onSelectChange,
    selections: [
      {
        key: 'selectAll',
        text: 'Select all',
        onSelect: changeableRowKeys => {
          setSelectedRowKeys(changeableRowKeys)
        }
      },
      {
        key: 'deslectAll',
        text: 'Deselect all',
        onSelect: () => {
          setSelectedRowKeys([])
        }
      }
    ],
    columnWidth: '3.75rem',
    getCheckboxProps: record => ({
      disabled: record.status === 'Closed'
    })
  }

  const clearSelection = () => {
    setSelectedRowKeys([])
  }

  return (
    <>
      <TableWrapper>
        <Table
          id={tableId}
          bordered={false}
          dataSource={formattedDisplayItems}
          rowKey={row =>
            // If a project level alert prefix the key with PROJECT_LEVEL_ALERTS_PREFIX to ensure uniqueness and allow for easier identification later
            row.isProjectLevelAlert
              ? `${PROJECT_LEVEL_ALERTS_PREFIX}${row.eventKey}`
              : row.eventKey || ''
          }
          columns={columns}
          onChange={handleTableChange}
          rowSelection={allowSelectionAndAction ? rowSelection : undefined}
          scroll={{ y: `calc(100vh - ${usedHeight}px)` }}
          size="middle"
          sticky
          pagination={false}
          locale={{
            emptyText: (
              <ZeroRecordsFooter>
                <icons.ManageSearch />
                <div>
                  No alerts matched your search. Try increasing the date range.
                </div>
              </ZeroRecordsFooter>
            )
          }}
          footer={currentPageData => {
            setFilteredDataCount(currentPageData.length)
            return null
          }}
        />
      </TableWrapper>

      <TableFooter>
        <ShowingAlertsText data-testid="totalInPage">
          Showing {filteredDataCount} out of {filteredDataCount} total alert
          {filteredDataCount === 1 ? '' : 's'}
        </ShowingAlertsText>
        <ButtonContainer>
          <BTTButton id="BTTButton" onClick={scrollToTop}>
            Back to top
            <Spacer.Fine />
            <icons.ChevronRight size={sizes.SMALL} rotate={-90} />
          </BTTButton>
        </ButtonContainer>
      </TableFooter>

      <ActionStrip
        eventKeys={selectedRowKeys.map(key => String(key))}
        eventRecordMapping={eventRecordMapping}
        selectedEventsContainUnloaded={false}
        onClose={reloadNeeded => {
          clearSelection()
          if (reloadNeeded) {
            reload()
          }
        }}
      />
    </>
  )
}

const ResetFilters = styled.button`
  height: 32px;
  width: 32px;
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  border: none;
  background-color: transparent;

  &:disabled {
    cursor: not-allowed;
    color: ${colours.BATTLESHIP_GREY};
  }
`
const CapitalisedText = styled(Typography.Text)`
  text-transform: capitalize;
`
